import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import {
  Menu,
  X,
  Home,
  Search,
  FileText,
  Users,
  BarChart3,
  Shield,
  LogOut,
  User,
  CheckCircle,
  Database
} from 'lucide-react';
import BrandMark from './BrandMark';
import NovoVisualAviso from './NovoVisualAviso';
import { telaNova } from '../config/novoVisual';
import '../styles/rf-ui.css';
// Depois de rf-ui.css de propósito: a ponte remapeia as utilitárias legadas
// para os tokens da marca, escopada em .rf-shell. Ver o cabeçalho do arquivo
// para o custo assumido e a condição de remoção.
import '../styles/rf-bridge.css';

const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isGlobalAdmin =
    user?.role === 'super_admin' ||
    user?.role === 'admin' ||
    user?.is_admin === true;
  const isClientAdmin = user?.role === 'client_admin' || user?.is_client_admin;
  const isInvestigator = user?.role === 'investigador_externo' || user?.is_investigador_externo === true;

  // Menu items baseado no role do usuário
  const getMenuItems = () => {
    const baseItems = [
      {
        name: 'Dashboard',
        path: '/admin/dashboard',
        icon: Home,
        roles: ['super_admin', 'admin', 'client_admin']
      },
      {
        name: 'Investigação',
        path: '/investigation',
        icon: Search,
        roles: ['super_admin', 'admin', 'client_admin', 'investigador_externo']
      },
      {
        name: 'Analytics',
        path: '/analytics-disruptive',
        icon: BarChart3,
        roles: ['super_admin', 'admin', 'client_admin']
      }
    ];

    const adminItems = [
      {
        name: 'Aprovação de Denúncias',
        path: '/approval',
        icon: CheckCircle,
        roles: ['super_admin', 'admin'],
        requiresAdminOrInvestigator: true // Flag especial para verificar is_admin ou is_investigador_externo
      },
      {
        name: 'Políticas IA',
        path: '/admin/policies',
        icon: Database,
        roles: ['super_admin', 'admin']
      },
      {
        name: 'Usuários',
        path: '/users',
        icon: Users,
        roles: ['super_admin', 'admin']
      },
      {
        name: 'Formulários',
        path: '/forms',
        icon: FileText,
        roles: ['super_admin', 'admin']
      },
      {
        name: 'Auditoria',
        path: '/audit',
        icon: Shield,
        roles: ['super_admin', 'admin']
      }
    ];

    // Filtrar itens baseado no role do usuário
    const userRole = user?.role || 'user';
    const allItems = [...baseItems, ...adminItems];
    
    return allItems.filter(item => {
      // Verificação especial para itens que requerem admin ou investigador
      if (item.requiresAdminOrInvestigator) {
        return isGlobalAdmin || isInvestigator;
      }

      if (item.path === '/admin/policies') {
        return isGlobalAdmin;
      }

      return item.roles.includes(userRole) ||
        (isGlobalAdmin && item.roles.includes('admin')) ||
        (isClientAdmin && item.roles.includes('client_admin'));
    });
  };

  const menuItems = getMenuItems();

  // Ao estreitar para mobile, a sidebar volta a ser gaveta: fecha e desfaz o
  // colapso. O deslocamento do conteúdo NÃO é calculado aqui — em telas grandes
  // a sidebar é um item do flex e o `flex-1` do conteúdo já ocupa o resto.
  // Antes havia um marginLeft em estado somado à largura da sidebar, o que
  // deslocava o conteúdo duas vezes (288px do flex + 256px de margem) e jogava
  // o painel para fora da viewport.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
        setSidebarCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fechar sidebar ao navegar em mobile
  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const papel =
    user?.role === 'super_admin' ? 'Super Admin' :
    user?.role === 'admin' ? 'Administrador' :
    user?.role === 'client_admin' ? 'Admin Cliente' :
    user?.role === 'investigador_externo' ? 'Investigador' :
    'Usuário';

  const tituloDaPagina =
    menuItems.find((item) => item.path === location.pathname)?.name || 'Dashboard';

  const subtituloDaPagina =
    location.pathname === '/investigation' ? 'Centro de Investigação' :
    location.pathname === '/admin/dashboard' ? 'Painel Administrativo' :
    'Sistema de Gestão';

  return (
    // Toda a casca usa classes `rf-` (styles/rf-ui.css) em vez de utilitárias
    // do Tailwind: os CSS legados redefinem 88 delas e carregam depois no
    // bundle, vencendo por ordem de fonte. Foi assim que `.min-h-screen`
    // (redefinida como flex-column) virou esta casca de lado.
    <div className="rf-shell">
      {sidebarOpen && (
        <div className="rf-scrim" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      <aside
        className={[
          'rf-sidebar',
          sidebarOpen ? 'is-open' : '',
          sidebarCollapsed ? 'is-collapsed' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="rf-sidebar__head">
          {/* tone="dark" = arte clara, para fundo escuro. */}
          <BrandMark tone="dark" height={sidebarCollapsed ? 34 : 40} decorative />
          <div className="rf-sidebar__ident">
            <p className="rf-sidebar__name">REPORTFius</p>
            <p className="rf-sidebar__tag">Sistema de Denúncias</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="rf-sidebar__close"
            aria-label="Fechar menu"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <nav className="rf-nav" aria-label="Navegação principal">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const ehNova = Boolean(telaNova(item.path));

            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`rf-navitem${isActive ? ' is-active' : ''}`}
                title={sidebarCollapsed ? item.name : undefined}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon size={19} className="rf-navitem__icon" aria-hidden="true" />
                <span className="rf-navitem__text">{item.name}</span>
                {/* Selo some sozinho depois da janela de destaque — ver
                    config/novoVisual.js. "Novo" permanente não significa nada. */}
                {ehNova && !sidebarCollapsed && <span className="rf-new">Novo</span>}
              </button>
            );
          })}
        </nav>

        <div className="rf-sidebar__foot">
          <div className="rf-user">
            <span className="rf-user__avatar">
              <User size={18} aria-hidden="true" />
            </span>
            <div className="rf-user__ident" style={{ minWidth: 0, flex: 1 }}>
              <p className="rf-user__name">{user?.full_name || user?.username || 'Usuário'}</p>
              <p className="rf-user__role">{papel}</p>
            </div>
          </div>
          {!sidebarCollapsed && (
            <button onClick={logout} className="rf-logout">
              <LogOut size={15} aria-hidden="true" />
              Sair
            </button>
          )}
        </div>
      </aside>

      <div className="rf-main">
        <header className="rf-topbar">
          <button
            onClick={toggleSidebar}
            className="rf-iconbtn"
            aria-label={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <Menu size={19} aria-hidden="true" />
          </button>
          <div style={{ minWidth: 0 }}>
            <h1 className="rf-topbar__title">{tituloDaPagina}</h1>
            <p className="rf-topbar__sub">{subtituloDaPagina}</p>
          </div>
        </header>

        {/* Aviso de primeira visita, só nas telas já redesenhadas. */}
        <NovoVisualAviso rota={location.pathname} />

        {/* `flex-1` e não `min-h-screen`: a casca já garante a altura da tela, e
            forçar 100vh aqui somava a altura do topo e criava rolagem vazia. */}
        <main className="rf-page">{children}</main>
      </div>
    </div>
  );
};

export default MainLayout;
